/**
 * AdoraCare - the CONTINUE-TREATMENT header + the NEXT VISIT panel
 * (DOCTOR-FLOW-AFTER-INTAKE-V1 D1 + D3; founder rulings 2026-08-15 + the
 * mockup MATCH with clarifications).
 *
 * D3 -- ContinueTreatmentHeader: the returning course patient's consult opens
 * on "Continue treatment - Stage N of M" with the derived stage ladder --
 * never visually from zero. DERIVE-ONLY (consult-stage-view.ts over the
 * page-lifted journey read); renders nothing without an honest ladder.
 *
 * D1 -- NextVisitPanel: the structured next-visit INTENT. The intent rides
 * the SHEET's existing follow-up fields (follow_up_option / follow_up_note;
 * treatment doctor via D2's treatment_doctor_id) and reaches Reception
 * through the EXISTING lawful owners: the checkout-handoff read and the
 * desk's derived "To schedule" queue (work-actions scheduleRows). ONE
 * behavior for lead-linked and golden patients alike.
 *
 * DOCTOR-HANDS-V1 (BUILD 1) -- THIS HEADER'S OLD CLAIM IS RETIRED. It read
 * "the doctor cannot book (the frozen booking audience denies the doctor by
 * construction)", and on the DESK'S CRM route that is still exactly true:
 * APPOINTMENT_CREATE_ROLES (api/routes/appointments.ts:180-181) is
 * untouched by this package, and its config union still rejects `doctor` by
 * construction. What changed is that the doctor now has a SEPARATE, NARROWER
 * door of her own -- POST /api/doctor/consult/:id/next-visit-booking, fenced
 * by the doctor-only audience + the org fence + the clinic fence, with the
 * patient and clinic DERIVED from the fenced consultation -- so she can book
 * the follow-up for the patient in front of her and nothing else. The INTENT
 * above is unchanged and still hands off to the desk; the booking door is
 * ADDITIVE, and it is absent when the visit has no golden patient record.
 * The offer behind it is the CANONICAL RegisterBookingOffer over THE ONE
 * DaySlotPicker -- reused, never a second booking path.
 *
 * INTERACTION LAW (the #560 R2 idiom, applied over the mockup's literal
 * Send button -- recorded as a mockup delta): the chip tap IS the act
 * (autosave persists it; the status line confirms at the point of the act);
 * no ceremony button, no second save. The SUGGESTED chip is an HONEST
 * derivation only (the course's own last recorded interval) -- when none
 * exists nothing is suggested.
 */

import { ConsultReturnBooking, returnDay, type ReturnBookingChannel } from './ConsultReturnBooking';
import type {
  BookedAppointmentResult,
  ClinicalReturnRequestView,
  CaseSheetFollowUpOptionView,
  DaySlotsResponse,
  DoctorConsultContext,
} from '@/dashboard/api';
import { AcBadge, AcButton, AcInput, AcSelect, DateField } from '@/dashboard/ui';
import RegisterBookingOffer from '@/dashboard/patients/RegisterBookingOffer';
import { formatIstDateTime, formatIstShortDate } from '@/lib/datetime-ist';
import { IntakeChoiceChips } from '@/dashboard/appointments/PatientIntakeScreens';
import {
  FOLLOW_UP_LABELS,
  RETURN_REQUEST_CHOICES, returnRequestDate, returnRequestProblem, returnRequestsEqual,
  followUpSelectionValid,
  handoffCurrentOption,
  handoffReachLine,
  handoffRosterView,
  HANDOFF_ROSTER_TRUNCATED_COPY,
  type StructuredSheetForm,
} from '@/dashboard/doctor/doctor-consult-view';
import {
  continueHeaderTitle,
  nextVisitStageTitle,
  stageCourseLine,
  type CourseStageLadder,
} from '@/dashboard/doctor/consult-stage-view';
import { useRef, useState } from 'react';

/** The structural free-text escape key (mirrors the form vocabulary). */
const CUSTOM_KEY = 'custom';

/** DOCTOR-HANDS-V1 (BUILD 1) -- the doctor's own booking door.
 *
 * THE WALK'S SHARPEST FLOOR GAP (step 12): "she records an intent; she
 * cannot book -- denied by construction." The intent still rides the sheet
 * and still reaches the desk exactly as before; what is added is the act
 * she performs herself, without leaving the room or asking the front desk.
 *
 * THE COMPONENT IS NOT NEW. This mounts RegisterBookingOffer -- the SAME
 * canonical booking offer the desk uses after registration and the walk-in
 * To-schedule queue uses for its Book door -- composing THE ONE
 * DaySlotPicker (the ONE-PICKER LAW). No second booking path is built: only
 * the FENCE behind it differs, injected as `channel`, because the desk's
 * frozen booking audience denies the doctor and config cannot open it.
 */
export interface NextVisitBookingProps {
  readonly identity?: string;
  readonly clinicName?: string;
  readonly currentDoctorId?: number | null;
  readonly returnChannel?: ReturnBookingChannel;
  readonly patientName: string;
  /** The golden record behind this visit. The SERVER derives its own copy
   *  from the fenced consultation and refuses a client-sent patientId --
   *  this value is display + component contract only, never authority. */
  readonly patientId: string;
  readonly clinicId: number | null;
  /** The proposed clinic-day from the chosen follow-up interval, or '' when
   *  the token carries no structured interval (absence, never an invented
   *  date). */
  readonly proposedDate: string;
  readonly channel: {
    readonly fetchSlots: (date: string) => Promise<DaySlotsResponse>;
    readonly book: (startIso: string) => Promise<BookedAppointmentResult>;
  };
}

/** The door's copy -- what she is about to do, in her own words. */
export const NEXT_VISIT_BOOK_DOOR = 'Book the next visit now';
export const NEXT_VISIT_BOOKED_ACTION = 'Back to the consultation';
export const NEXT_VISIT_BOOK_HEADLINE =
  'Book this patient\'s next visit. The front desk sees it on the day board.';

/** Stable EMPTY requirement -- the default for every mount that is not
 *  procedure-scoped (a fresh [] literal per render would churn the prop). */
const EMPTY_REQUIRED_SPECIALTIES: readonly string[] = Object.freeze([]);

/** Cap the rendered ladder steps (CHIP-law density; the course card's own
 *  ledger lists every line -- the header is orientation, not the record). */
const MAX_LADDER_STEPS = 6;

export function ContinueTreatmentHeader({
  ladder,
}: {
  ladder: CourseStageLadder | null;
}): JSX.Element | null {
  if (ladder === null) return null;
  const overflow = ladder.steps.length - MAX_LADDER_STEPS;
  const steps = ladder.steps.slice(0, MAX_LADDER_STEPS);
  return (
    <section
      className="ac-section consult-continue"
      data-testid="consult-continue-header"
    >
      <p className="consult-continue__title" data-testid="consult-continue-title">
        {continueHeaderTitle(ladder)}
      </p>
      <p className="consult-continue__course">{stageCourseLine(ladder)}</p>
      <div className="consult-continue__ladder" data-testid="consult-stage-ladder">
        {steps.map((s, i) => {
          const isCurrent = ladder.nextStage?.itemId === s.itemId;
          const tone = s.done
            ? 'consult-stage consult-stage--done'
            : isCurrent
              ? 'consult-stage consult-stage--now'
              : 'consult-stage';
          return (
            <span key={s.itemId} className={tone}>
              <span className="consult-stage__n">{i + 1}</span>
              <span className="consult-stage__name">{s.name}</span>
              {s.done ? <span className="consult-stage__mark">done</span> : null}
              {isCurrent ? (
                <span className="consult-stage__mark">today</span>
              ) : null}
            </span>
          );
        })}
        {overflow > 0 ? (
          <span className="consult-stage">
            <span className="consult-stage__name">{`+${overflow} more`}</span>
          </span>
        ) : null}
      </div>
    </section>
  );
}

export function NextVisitPanel({
  ladder,
  followUpOptions,
  suggestedOption,
  form,
  sheetFinalized,
  attribution,
  clinicDoctors,
  visitClinicId,
  rosterTruncated = false,
  busy,
  onFormChange,
  booking,
  compact = false,
  returnRequestAvailable = false, savedRequest = null, returnCandidates = [],
}: {
  ladder: CourseStageLadder | null;
  followUpOptions: readonly CaseSheetFollowUpOptionView[];
  /** The course's own last recorded interval (honest derivation) or null. */
  suggestedOption: string | null;
  form: StructuredSheetForm;
  sheetFinalized: boolean;
  attribution: DoctorConsultContext['attribution'] | null;
  clinicDoctors: NonNullable<DoctorConsultContext['clinicDoctors']>;
  visitClinicId: number | null;
  /** The wire's clinicDoctorsTruncated flag -- when true the picker SAYS the
   *  roster is incomplete (never silence over a capped list). */
  rosterTruncated?: boolean;
  busy: boolean;
  onFormChange: (form: StructuredSheetForm) => void;
  /** DOCTOR-HANDS-V1 (BUILD 1): the doctor's OWN booking door. Absent = the
   *  pre-package panel exactly (intent only, the desk schedules) -- which is
   *  still the honest state for a visit with no golden patient record. */
  booking?: NextVisitBookingProps;
  /** Approved C: keep the follow-up choice in an inline disclosure. */
  compact?: boolean;
  returnRequestAvailable?: boolean;
  savedRequest?: ClinicalReturnRequestView | null;
  returnCandidates?: readonly {id:number;label:string}[];
}): JSX.Element {
  const [noteOpen, setNoteOpen] = useState(false);
  const [bookOpen, setBookOpen] = useState(false);
  const [bookedIso, setBookedIso] = useState<string | null>(null);
  const [bookingPending, setBookingPending] = useState(false);
  const bookingResult = useRef<BookedAppointmentResult | null>(null);
  const [doctorOpen, setDoctorOpen] = useState(false);
  const [doctorQuery, setDoctorQuery] = useState('');

  // A time is a choice for this request, not just for its date. Reuse the
  // shared picker's reset-on-mount behavior when the doctor changes it.
  const request = form.returnRequest;
  const [returnRoster,setReturnRoster]=useState<{key:string;dates:readonly string[]|null;error:boolean}|null>(null);
  const requestKey = returnRequestAvailable ? JSON.stringify([
    request?.timing, request?.basedOnDate, request?.onDate,
    request?.interval?.count, request?.interval?.unit, request?.forPlanItemId,
    request?.requestedDoctorId, request?.durationMinutes,
  ]) : 'legacy';
  const pickerKey = useRef(requestKey);
  // Do not detach an outstanding write from its result if fresh consultation
  // data arrives while it is running. Its saved result remains authoritative.
  if (!bookingPending) pickerKey.current = requestKey;
  const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
  const canBookRequest = !returnRequestAvailable || (
    returnRequestDate(request) !== null && returnRequestProblem(request, savedRequest, today) === null
  );

  const stageTitle = ladder !== null ? nextVisitStageTitle(ladder) : null;
  const needsNote = !followUpSelectionValid(form);
  const showNote = noteOpen || form.followUpNote !== '' || needsNote;
  const picked = form.followUpOption !== '';

  const attributionLive = attribution?.available === true;
  const currentDoctorId =
    form.treatmentDoctorId ??
    attribution?.treatmentDoctorId ??
    attribution?.assignedDoctorId ??
    null;
  // Identity is separate from the mutable treatment/default selection.
  const actingDoctorId = booking?.currentDoctorId ?? null;
  const rosterName = (id: number | null): string | null => {
    if (id === null) return null;
    return clinicDoctors.find((d) => d.id === id)?.name ?? null;
  };
  const currentDoctorName =
    rosterName(currentDoctorId) ??
    attribution?.treatmentDoctorName ??
    attribution?.assignedDoctorName ??
    null;
  // DOCTOR-FINDABILITY-AT-SCALE-V1: the FS-19 cross-branch handoff keeps the
  // candidate set org-wide, and at the confirmed 253-doctor structure a flat
  // dump is unusable -- the picker rests on the visit's own clinic and
  // searches the WHOLE wire roster by name (the workspace's existing W-B1
  // search idiom; derivations are the pure doctor-consult-view helpers).

  const chipOptions = [
    ...followUpOptions.map((o) => ({
      code: o.key,
      label:
        suggestedOption === o.key && !picked
          ? `${o.label} - suggested`
          : o.label,
    })),
    { code: CUSTOM_KEY, label: 'Other' },
  ];

  // One existing booking owner serves both legacy and structured Return.
  const bookingContent = returnRequestAvailable && booking?.returnChannel ? <ConsultReturnBooking
    key={booking.identity} identity={booking.identity ?? booking.patientId} channel={booking.returnChannel}
    requestKey={requestKey} targetDate={returnRequestDate(request)} doctorId={request?.requestedDoctorId ?? null}
    durationMinutes={request?.durationMinutes ?? null} doctorName={rosterName(request?.requestedDoctorId ?? null) ?? 'Not selected'}
    requestDetails={request?.details} requestStatus={request&&request.timing!=='none'?{dirty:!returnRequestsEqual(request,savedRequest),after:request.timing==='after_treatment'}:undefined} currentDoctorId={actingDoctorId} doctorNames={Object.fromEntries(clinicDoctors.map(d=>[d.id,d.name]))} clinicName={booking.clinicName ?? 'This clinic'} canBook={canBookRequest} disabled={busy || sheetFinalized || returnRequestProblem(request, savedRequest, today) !== null}
    onPending={setBookingPending} onSaved={setBookedIso} onRoster={setReturnRoster} /> : booking !== undefined && !sheetFinalized && (canBookRequest || bookedIso !== null || bookingPending) ? (
        <div className="consult-nextvisit__book" data-testid="consult-nextvisit-book">
          {bookedIso !== null ? (
            <p
              className="consult-frow__sub"
              data-testid="consult-nextvisit-booked"
              role="status"
            >
              {`Next visit booked for ${formatIstDateTime(bookedIso)} (clinic time). The front desk sees it on the day board.`}
            </p>
          ) : bookOpen ? (
            <RegisterBookingOffer
              key={pickerKey.current}
              patientName={booking.patientName}
              headline={NEXT_VISIT_BOOK_HEADLINE}
              patientId={booking.patientId}
              // The doctor's door is patient-keyed and derives its own
              // identity server-side; there is no lead leg here.
              leadId={null}
              clinicId={booking.clinicId}
              // PROPOSE-THEN-CONFIRM: the chosen interval opens the picker on
              // the proposed clinic-day. She still picks the slot; nothing
              // auto-books. Derived by THE ONE existing helper the desk's
              // To-schedule Book door already uses -- no second date rule.
              initialPickerDate={booking.proposedDate}
              bookingChannel={returnRequestAvailable ? {
                fetchSlots: booking.channel.fetchSlots,
                book: async (startIso) => {
                  const result = await booking.channel.book(startIso);
                  bookingResult.current = result;
                  return result;
                },
              } : booking.channel}
              onBusyChange={setBookingPending}
              onBookingSaved={returnRequestAvailable ? (iso) => {
                if (!['booked', 'confirmed'].includes(bookingResult.current?.appointment.status ?? '')) return;
                setBookedIso(iso);
                setBookingPending(false);
                setBookOpen(false);
              } : undefined}
              // She is mid-consultation: the desk's board jump is the wrong
              // next step here, so the booked-state action returns her to the
              // patient in front of her. The offer's own confirmation states
              // the LANDED clinic-local time first (a wrong-day landing is
              // never silent), and the panel keeps a persistent line after.
              bookedActionLabel={NEXT_VISIT_BOOKED_ACTION}
              onBooked={(_appointmentId, iso) => {
                if (!returnRequestAvailable || ['booked', 'confirmed'].includes(bookingResult.current?.appointment.status ?? '')) setBookedIso(iso);
                setBookOpen(false);
              }}
              onSkip={() => setBookOpen(false)}
            />
          ) : (
            <AcButton
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={() => setBookOpen(true)}
              data-testid="consult-nextvisit-book-door"
            >
              {returnRequestAvailable ? 'View availability / Book now' : NEXT_VISIT_BOOK_DOOR}
            </AcButton>
          )}
        </div>
      ) : null;

  if(returnRequestAvailable)return <StructuredReturnVisit form={form} savedRequest={savedRequest} sheetFinalized={sheetFinalized} busy={busy || bookingPending}
    clinicDoctors={clinicDoctors} visitClinicId={visitClinicId} rosterTruncated={rosterTruncated} candidates={returnCandidates} onFormChange={onFormChange} integratedBooking={!!booking?.returnChannel} bookingContent={bookingContent} bookedIso={bookedIso} roster={returnRoster?.key===requestKey ? returnRoster : null} defaultDoctorId={currentDoctorId} actingDoctorId={actingDoctorId} clinicName={booking?.clinicName ?? null} />;

  const FollowUpContainer = compact ? 'details' : 'div';
  return (
    <section className="ac-section consult-nextvisit" data-testid="consult-nextvisit">
      <div className="consult-card-head">
        <span className="consult-card-head__t">Next visit</span>
        <span className="consult-card-head__grow" />
        {picked ? (
          <AcBadge tone="positive" testId="consult-nextvisit-state">
            {sheetFinalized
              ? 'Sent to Front Desk to schedule'
              : 'Sent to Front Desk at close'}
          </AcBadge>
        ) : null}
      </div>
      {stageTitle !== null ? (
        <p className="consult-nextvisit__stage" data-testid="consult-nextvisit-title">
          {stageTitle}
        </p>
      ) : null}
      <p className="consult-frow__sub">
        {stageTitle !== null
          ? 'Suggested from the course ladder - the front desk schedules from this.'
          : 'What happens next for this patient - the front desk schedules from this.'}
      </p>
      {/* NAME THE FIELD (founder finding, #780 review). The chip group carried
          an `ariaLabel` and no VISIBLE label, so on a visit with nothing
          picked the doctor saw a lone chip reading "Other" with nothing
          saying what it answers. The accessible name is unchanged -- this
          adds the visible one it was missing, from the same label dictionary
          so the two can never drift. */}
      <FollowUpContainer className="consult-nextvisit__choices" data-testid="consult-nextvisit-choices">
      {compact ? <summary>
        <span>{FOLLOW_UP_LABELS.option}</span>
        <strong>{chipOptions.find(option => option.code === form.followUpOption)?.label ?? 'Not selected'}</strong>
      </summary> : null}
      <span className="clinic-workbench__label" data-testid="consult-nextvisit-option-label">
        {FOLLOW_UP_LABELS.option}
      </span>
      <IntakeChoiceChips
        ariaLabel={FOLLOW_UP_LABELS.option}
        options={chipOptions}
        isSelected={(code) => form.followUpOption === code}
        onPick={(code) =>
          onFormChange({
            ...form,
            followUpOption: form.followUpOption === code ? '' : code,
          })
        }
        disabled={busy || sheetFinalized}
        testId="consult-nextvisit-opt"
      />
      </FollowUpContainer>
      {picked && !sheetFinalized ? (
        <p className="consult-frow__sub" data-testid="consult-nextvisit-note-line">
          Reaches the Front Desk queue when the visit closes.
        </p>
      ) : null}
      <div className="consult-nextvisit__meta">
        {attributionLive ? (
          <span data-testid="consult-nextvisit-doctor">
            {`With: ${currentDoctorName ?? 'Unassigned'}`}
            {!sheetFinalized ? (
              <AcButton
                size="sm"
                variant="ghost"
                onClick={() => setDoctorOpen((v) => !v)}
                data-testid="consult-nextvisit-doctor-change"
              >
                Change
              </AcButton>
            ) : null}
          </span>
        ) : null}
        {!sheetFinalized ? (
          <AcButton
            size="sm"
            variant="ghost"
            onClick={() => setNoteOpen((v) => !v)}
            data-testid="consult-nextvisit-note-door"
          >
            {showNote ? 'Hide note' : 'Add note for the desk'}
          </AcButton>
        ) : null}
      </div>
      {doctorOpen && attributionLive && !sheetFinalized ? (
        <HandoffDoctorPicker
          clinicDoctors={clinicDoctors}
          visitClinicId={visitClinicId}
          rosterTruncated={rosterTruncated}
          currentDoctorId={currentDoctorId}
          currentDoctorName={currentDoctorName}
          busy={busy}
          query={doctorQuery}
          onQueryChange={setDoctorQuery}
          onPickDoctor={(id) =>
            onFormChange({ ...form, treatmentDoctorId: id })
          }
        />
      ) : null}
      {bookingContent}
      {showNote && !sheetFinalized ? (
        <label
          className="clinic-workbench__field consult-sheet__field"
          htmlFor="consult-followup-note"
          title="Optional for a listed option; required for Other. Booking instructions only - clinical detail stays in the notes."
        >
          <span>{FOLLOW_UP_LABELS.note}</span>
          <textarea
            id="consult-followup-note"
            className="clinic-workbench__input cockpit-notes__area"
            data-testid="consult-followup-note"
            rows={2}
            value={form.followUpNote}
            disabled={busy}
            aria-invalid={needsNote}
            aria-describedby={
              needsNote ? 'consult-followup-note-required' : undefined
            }
            onChange={(e) => onFormChange({ ...form, followUpNote: e.target.value })}
          />
          {needsNote ? (
            <small
              id="consult-followup-note-required"
              className="clinic-workbench__muted"
              role="alert"
              data-testid="consult-followup-note-required"
            >
              Add the instruction for the front desk - an &quot;Other&quot;
              follow-up needs it before it can save.
            </small>
          ) : null}
        </label>
      ) : null}
    </section>
  );
}

/**
 * The treatment-doctor handoff picker (DOCTOR-FINDABILITY-AT-SCALE-V1).
 * Pure render over the pure doctor-consult-view derivations (query state is
 * the parent's), so the search states are statically testable in the repo
 * idiom. The select, its option grammar and its write path are the EXISTING
 * ones -- only WHO CAN BE FOUND changed:
 *   - resting = the visit's own clinic (never the org dump);
 *   - a typed name searches the WHOLE wire roster (FS-19 full reach);
 *   - the reach line + the truncation notice keep every state honest -- a
 *     scoped or capped list is never presented as complete.
 */
export function HandoffDoctorPicker({
  clinicDoctors,
  visitClinicId,
  rosterTruncated,
  currentDoctorId,
  currentDoctorName,
  busy,
  query,
  onQueryChange,
  onPickDoctor,
  instanceId = 'consult-nextvisit-doctor',
  fieldLabel = 'Treatment doctor',
  placeholderLabel = 'Visit doctor (default)',
  searchAriaLabel = 'Search doctors by name',
  truncatedTestId = 'consult-nextvisit-roster-truncated',
  requiredSpecialties = EMPTY_REQUIRED_SPECIALTIES,
}: {
  clinicDoctors: NonNullable<DoctorConsultContext['clinicDoctors']>;
  visitClinicId: number | null;
  rosterTruncated: boolean;
  currentDoctorId: number | null;
  currentDoctorName: string | null;
  busy: boolean;
  query: string;
  onQueryChange: (query: string) => void;
  onPickDoctor: (doctorId: number | null) => void;
  /** DOCTOR-CONSULT-DELTA-V1: the DOM id / testid stem, so a SECOND mount of
   *  THE ONE picker (the treatment-specific per-line assignment) cannot
   *  collide with the visit-grain handoff's ids. Defaults to the handoff's
   *  own stem, so that mount stays byte-identical. */
  instanceId?: string;
  /** The field's own name (the two mounts assign DIFFERENT facts). */
  fieldLabel?: string;
  /** The empty-selection option's copy (what "no pick" means here). */
  placeholderLabel?: string;
  /** The search box's accessible name -- defaulted to the handoff mount's
   *  existing copy so that mount stays byte-identical. */
  searchAriaLabel?: string;
  /** The truncation notice's testid. It does NOT follow the instance stem:
   *  the handoff mount's existing testid predates the stem and is pinned, so
   *  it is defaulted here verbatim rather than re-derived. */
  truncatedTestId?: string;
  /** DOCTOR-HANDS-V1 (BUILD 2): the specialty tokens recorded for THIS
   *  line's procedure, off the consult wire's plan-scoped capability map.
   *  EMPTY (the default) = unmapped = the resting list is byte-identical to
   *  the pre-package clinic list, which is exactly what the VISIT-grain
   *  mount passes -- a visit has no single procedure to be capable of. */
  requiredSpecialties?: readonly string[];
}): JSX.Element {
  const roster = handoffRosterView(
    clinicDoctors,
    visitClinicId,
    query,
    requiredSpecialties,
  );
  const options = roster.options;
  const currentOption = handoffCurrentOption(
    options,
    currentDoctorId,
    currentDoctorName,
  );
  return (
    <label
      className="clinic-workbench__field consult-nextvisit__doctor"
      htmlFor={`${instanceId}-select`}
    >
      <span>{fieldLabel}</span>
      <input
        type="search"
        id={`${instanceId}-search`}
        className="clinic-workbench__input"
        data-testid={`${instanceId}-search`}
        placeholder="Search doctors by name"
        aria-label={searchAriaLabel}
        value={query}
        disabled={busy}
        autoComplete="off"
        onChange={(e) => onQueryChange(e.target.value)}
      />
      <small className="clinic-workbench__muted" data-testid={`${instanceId}-reach`}>
        {handoffReachLine(
          clinicDoctors.length,
          rosterTruncated,
          query,
          options.length,
          {
            filtered: roster.capabilityFiltered,
            released: roster.capabilityReleased,
            clinicCount: roster.clinicCount,
          },
        )}
      </small>
      {rosterTruncated ? (
        <small
          className="clinic-workbench__muted"
          role="alert"
          data-testid={truncatedTestId}
        >
          {HANDOFF_ROSTER_TRUNCATED_COPY}
        </small>
      ) : null}
      <select
        id={`${instanceId}-select`}
        className="clinic-workbench__input"
        data-testid={`${instanceId}-select`}
        aria-label={fieldLabel}
        value={currentDoctorId ?? ''}
        disabled={busy}
        onChange={(e) =>
          onPickDoctor(e.target.value === '' ? null : Number(e.target.value))
        }
      >
        <option value="">{placeholderLabel}</option>
        {currentOption !== null ? (
          <option key={currentOption.id} value={currentOption.id}>
            {currentOption.label}
          </option>
        ) : null}
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}


/** V14g request fields. Uses the existing sheet save/recovery owner; booking
 * remains a separate canonical act and current attribution is never edited. */
function StructuredReturnVisit({ form, savedRequest, sheetFinalized, busy, clinicDoctors, visitClinicId, rosterTruncated, candidates, onFormChange, bookingContent, integratedBooking, bookedIso, defaultDoctorId, actingDoctorId, clinicName, roster }: {
  form: StructuredSheetForm; savedRequest: ClinicalReturnRequestView | null; sheetFinalized: boolean; busy: boolean;
  clinicDoctors: NonNullable<DoctorConsultContext['clinicDoctors']>; visitClinicId: number | null; rosterTruncated: boolean;
  candidates: readonly { id: number; label: string }[]; onFormChange: (form: StructuredSheetForm) => void;
  bookingContent: React.ReactNode; integratedBooking:boolean; bookedIso: string | null; defaultDoctorId: number | null; actingDoctorId: number | null; clinicName: string | null; roster:{dates:readonly string[]|null;error:boolean}|null;
}): JSX.Element {
  const [doctorOpen, setDoctorOpen] = useState(false);
  const [doctorQuery, setDoctorQuery] = useState('');
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [intervalUnit, setIntervalUnit] = useState<'days'|'weeks'|'months'>(form.returnRequest?.interval?.unit ?? 'weeks');
  const doctorChange=useRef<HTMLButtonElement>(null);
  const request = form.returnRequest ?? null;
  const disabled = busy || sheetFinalized;
  const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
  const makeRequest = (): ClinicalReturnRequestView => request ?? { version: 1, timing: 'other', basedOnDate: today, onDate: null, interval: null, forPlanItemId: null, details: '', requestedDoctorId: clinicDoctors.some(doctor => doctor.id === defaultDoctorId) ? defaultDoctorId : null, durationMinutes: null };
  const change = (patch: Partial<ClinicalReturnRequestView>) => onFormChange({ ...form, returnRequest: { ...makeRequest(), ...patch } });
  const requestProblem = returnRequestProblem(request, savedRequest, today);
  const problem = sheetFinalized ? null : requestProblem;
  const due = returnRequestDate(request);
  const none = request?.timing === 'none', after = request?.timing === 'after_treatment';
  const doctorId = request?.requestedDoctorId ?? null;
  const doctor = clinicDoctors.find(d => d.id === doctorId);
  const doctorName = doctor?.name ?? (doctorId === null ? 'Not selected' : 'Saved doctor unavailable in this roster');
  const missingTreatment = request?.forPlanItemId != null && !candidates.some(item => item.id === request.forPlanItemId);
  const dirty = !returnRequestsEqual(request, savedRequest);
  return <section className="ac-section consult-nextvisit consult-v14g__return" data-testid="consult-return-visit">
    <h3>Return visit <span className="consult-v14g__return-term">you set what is needed · Reception or COC books it with the patient</span></h3>
    <IntakeChoiceChips ariaLabel="When" options={RETURN_REQUEST_CHOICES} isSelected={code => request?.timing === code}
      onPick={code => change({ timing: code as ClinicalReturnRequestView['timing'], basedOnDate: today, ...(code==='none'?{interval:null,onDate:null}:{}) })} disabled={disabled} testId="consult-return-when" />
    {request?.timing === 'other' ? <div className="consult-v14g__return-other">
      <label htmlFor="consult-return-count">In</label><AcInput id="consult-return-count" aria-label="How many" placeholder="0" inputMode="numeric" autoComplete="off" pattern="[0-9]*"
        value={request.interval?.count ?? ''} disabled={disabled} aria-invalid={problem !== null}
        onChange={event => { const raw=event.target.value; if (/^\d*$/.test(raw)) change({interval:raw===''?null:{count:Number(raw),unit:request.interval?.unit??intervalUnit}}); }} />
      <AcSelect aria-label="Days, weeks or months" value={request.interval?.unit ?? intervalUnit} disabled={disabled}
        onChange={event => { const unit=event.target.value as typeof intervalUnit;setIntervalUnit(unit);if(request.interval)change({interval:{...request.interval,unit}}); }}>
        {(['days','weeks','months'] as const).map(unit=><option key={unit}>{unit}</option>)}
      </AcSelect><span>or on</span><DateField id="consult-return-date" label="On a date" value={request.onDate ?? ''} disabled={disabled} onChange={onDate=>change({onDate:onDate||null})} />
    </div> : null}
    {problem ? <p role="alert" className="consult-v14g__return-error">{problem} Nothing can be saved, printed or booked until it is fixed.</p> : null}
    {none ? <p className="consult-frow__sub">{bookedIso ? 'No additional return visit is requested. The appointment below is still booked — it stays until you cancel it.' : 'No return visit for this visit. Existing appointments and recalls stay as they are.'}</p> : request ? <>
      <div className="consult-v14g__return-grid">
        <span className="consult-v14g__return-label">Date</span><span>{after ? 'When the treatment ends — no date yet' : due ? <strong>{`${new Intl.DateTimeFormat('en-GB',{weekday:'short',timeZone:'UTC'}).format(new Date(due+'T12:00:00Z'))} ${formatIstShortDate(due)} ${due.slice(0,4)}`}</strong> : 'Type how many days, weeks or months, or pick a date.'}</span>
        <label className="consult-v14g__return-label" htmlFor="consult-return-treatment">For</label><div className="consult-v14g__return-for">
          {candidates.length > 0 || missingTreatment ? <AcSelect id="consult-return-treatment" aria-label="Which treatment this visit is for" value={request.forPlanItemId ?? 'review'} disabled={disabled}
            onChange={event=>change({forPlanItemId:event.target.value==='review'?null:Number(event.target.value)})}>
            {candidates.map(item=><option key={item.id} value={item.id}>{item.label}</option>)}
            {missingTreatment ? <option value={request.forPlanItemId!}>Saved treatment is no longer available for a return request</option> : null}
            <option value="review">Review only</option>
          </AcSelect> : null}
          {request.forPlanItemId===null || detailsOpen || request.details ? <AcInput aria-label="Reason for the visit" value={request.details} placeholder={request.forPlanItemId===null?'Review':undefined} maxLength={2000} disabled={disabled} onChange={event=>change({details:event.target.value})} /> : <AcButton variant="ghost" size="sm" disabled={disabled} onClick={()=>setDetailsOpen(true)}>Add details</AcButton>}
        </div>
        <span className="consult-v14g__return-label">With</span><div><strong>{doctorName}{doctorId!==null && actingDoctorId!==null && doctorId===actingDoctorId ? ' (you)' : ''}</strong>{doctor ? <span className="consult-frow__sub"> {doctor.specialty?.toLowerCase()}{doctor.specialty ? ' · ' : ''}{doctor.clinicId===visitClinicId ? clinicName ?? 'This clinic' : 'Other clinic'}</span> : null} {!sheetFinalized ? <AcButton ref={doctorChange} variant="ghost" size="sm" disabled={busy} onClick={()=>setDoctorOpen(value=>!value)}>Change</AcButton> : null}</div>
        <label className="consult-v14g__return-label" htmlFor="consult-return-duration">Time needed</label><AcSelect id="consult-return-duration" aria-label="Time needed" value={request.durationMinutes ?? ''} disabled={disabled}
          onChange={event=>change({durationMinutes:event.target.value===''?null:Number(event.target.value) as ClinicalReturnRequestView['durationMinutes']})}>
          <option value="">Not set</option>{[15,30,45,60,90].map(minutes=><option key={minutes} value={minutes}>{minutes} min</option>)}
        </AcSelect>
        {due&&!bookedIso&&integratedBooking ? <><span className="consult-v14g__return-label">At {clinicName??'this clinic'}</span><span className="consult-frow__sub">{returnRosterStatus(doctorId,request.durationMinutes,doctorName,clinicName,roster,requestProblem)}</span></> : null}
      </div>
      {doctorOpen && !sheetFinalized ? <HandoffDoctorPicker instanceId="consult-return-doctor" fieldLabel="Doctor for the return visit" placeholderLabel="Not selected" clinicDoctors={clinicDoctors} visitClinicId={visitClinicId} rosterTruncated={rosterTruncated} currentDoctorId={doctorId} currentDoctorName={doctorName} busy={busy} query={doctorQuery} onQueryChange={setDoctorQuery}
        onPickDoctor={id=>{change({requestedDoctorId:id});setDoctorOpen(false);requestAnimationFrame(()=>doctorChange.current?.focus());}} /> : null}
      {!bookedIso && !integratedBooking ? <div className="consult-v14g__return-status"><AcBadge tone="pending">{dirty ? 'Unsaved request' : 'Requested — not booked'}</AcBadge><span className="consult-frow__sub">{after ? 'Reception or COC books it when the treatment ends.' : 'Reception or COC books the time with the patient.'}</span></div> : null}
    </> : <p className="consult-frow__sub">Choose when, or No follow-up needed.</p>}
    {bookingContent}
  </section>;
}

/** Dated coverage cannot load until both required selections exist. */
export function returnRosterStatus(doctorId:number|null,durationMinutes:number|null,doctorName:string,clinicName:string|null,roster:{dates:readonly string[]|null;error:boolean}|null,problem:string|null=null):string {
  if(problem)return problem;
  if(doctorId===null||durationMinutes===null)return 'Choose the doctor and time needed to see availability.';
  if(roster?.error)return 'Roster unavailable';
  if(roster?.dates)return roster.dates.length ? doctorName+' works '+roster.dates.slice(0,3).map(returnDay).join(' · ') : doctorName+' is not at '+(clinicName??'this clinic')+' near that date';
  return 'Checking roster…';
}
